import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    travelDate: '',
    groupSize: '',
    accommodationType: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.alwaz.travel';
    const scripts: HTMLScriptElement[] = [];

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact Alwaz Travels | Book a Free Travel Consultation',
        description: 'Get in touch with Alwaz Travels London. Book a free travel consultation for Umrah packages, flights, Saudi relocation, or bespoke holidays. Call, WhatsApp, or email us.',
        url: `${siteUrl}/contact`,
        isPartOf: { '@type': 'WebSite', name: 'Alwaz Travels', url: siteUrl },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#business`,
        name: 'Alwaz Travels',
        alternateName: 'Nex Pro Travels Limited',
        url: siteUrl,
        telephone: '+442034884455',
        email: 'contact@alwaztravels.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'London',
          addressRegion: 'England',
          addressCountry: 'GB',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 51.5074,
          longitude: -0.1278,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Saturday'],
            opens: '10:00',
            closes: '16:00',
          },
        ],
        contactPoint: [
          {
            '@type': 'ContactPoint',
            telephone: '+442034884455',
            contactType: 'customer service',
            areaServed: 'GB',
            availableLanguage: ['English', 'Urdu', 'Arabic'],
          },
          {
            '@type': 'ContactPoint',
            telephone: '+447362505895',
            contactType: 'sales',
            contactOption: 'TollFree',
            areaServed: 'GB',
            availableLanguage: ['English', 'Urdu', 'Arabic'],
          },
        ],
      },
    ];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    document.title = 'Contact Alwaz Travels | Book a Free Travel Consultation – London';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Get in touch with Alwaz Travels London. Book a free travel consultation for Umrah packages, discounted flights, Saudi relocation, or bespoke holidays. Call 0203 4884455 or WhatsApp +44 7362 505895.');

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formDataToSend.append(key, value);
      });

      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          destination: '',
          travelDate: '',
          groupSize: '',
          accommodationType: '',
          budget: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/2b112216c0169cf900982614ed07e75a.jpg"
            alt="Contact Us"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl font-bold mb-4">Get in Touch</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Let our travel experts help you plan your perfect journey
          </p>
        </div>
      </section>

      {/* Contact Information & Form Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Our travel advisors are ready to assist you with personalized guidance and expert planning for your next journey.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-600 text-white rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a href="tel:02034884455" className="text-gray-600 hover:text-amber-600 transition-colors">
                      0203 4884455
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri: 9am-6pm GMT</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-600 text-white rounded-lg flex-shrink-0">
                    <i className="ri-whatsapp-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                    <a href="https://wa.me/447362505895" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-green-600 transition-colors">
                      +44 7362 505895
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Quick responses 24/7</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href="mailto:contact@alwaztravels.com" className="text-gray-600 hover:text-blue-600 transition-colors break-all">
                      contact@alwaztravels.com
                    </a>
                    <p className="text-sm text-gray-500 mt-1">We reply within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-700 text-white rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Office</h3>
                    <p className="text-gray-600">
                      Nex Pro Travels Limited<br />
                      London, United Kingdom
                    </p>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-time-line text-amber-600 mr-2"></i>
                  Office Hours
                </h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span className="font-medium">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span className="font-medium">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Consultation Booking Form */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Book a Free Consultation</h2>
                  <p className="text-gray-600">
                    Share your travel plans with us, and our expert advisors will create a personalized itinerary tailored to your needs.
                  </p>
                </div>

                <form 
                  id="consultation-booking"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d61rrqjn6d2i4h4oadig"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                        placeholder="+44 7XXX XXXXXX"
                      />
                    </div>
                  </div>

                  {/* Destination & Travel Date */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="destination" className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Destination <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="destination"
                        name="destination"
                        value={formData.destination}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer"
                      >
                        <option value="">Select destination</option>
                        <option value="Saudi Arabia - Umrah">Saudi Arabia - Umrah</option>
                        <option value="Saudi Arabia - Hajj">Saudi Arabia - Hajj</option>
                        <option value="Saudi Arabia - Tourism">Saudi Arabia - Tourism</option>
                        <option value="Turkey">Turkey</option>
                        <option value="UAE - Dubai">UAE - Dubai</option>
                        <option value="UAE - Abu Dhabi">UAE - Abu Dhabi</option>
                        <option value="Egypt">Egypt</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="travelDate" className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Travel Date
                      </label>
                      <input
                        type="date"
                        id="travelDate"
                        name="travelDate"
                        value={formData.travelDate}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Group Size & Accommodation Type */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="groupSize" className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Travelers <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="groupSize"
                        name="groupSize"
                        value={formData.groupSize}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer"
                      >
                        <option value="">Select group size</option>
                        <option value="1-2">1-2 people</option>
                        <option value="3-4">3-4 people</option>
                        <option value="5-8">5-8 people</option>
                        <option value="9-15">9-15 people</option>
                        <option value="16+">16+ people</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="accommodationType" className="block text-sm font-medium text-gray-700 mb-2">
                        Accommodation Preference
                      </label>
                      <select
                        id="accommodationType"
                        name="accommodationType"
                        value={formData.accommodationType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer"
                      >
                        <option value="">Select type</option>
                        <option value="Budget Hotel">Budget Hotel</option>
                        <option value="Standard Hotel">Standard Hotel</option>
                        <option value="Premium Hotel">Premium Hotel</option>
                        <option value="Luxury Hotel">Luxury Hotel</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Villa">Villa</option>
                        <option value="Heritage Stay">Heritage Stay</option>
                      </select>
                    </div>
                  </div>

                  {/* Budget */}
                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                      Budget Range (per person)
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer"
                    >
                      <option value="">Select budget range</option>
                      <option value="Under £1,000">Under £1,000</option>
                      <option value="£1,000 - £2,000">£1,000 - £2,000</option>
                      <option value="£2,000 - £3,500">£2,000 - £3,500</option>
                      <option value="£3,500 - £5,000">£3,500 - £5,000</option>
                      <option value="£5,000 - £8,000">£5,000 - £8,000</option>
                      <option value="Over £8,000">Over £8,000</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Details or Special Requirements
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm resize-none"
                      placeholder="Tell us about your travel preferences, dietary requirements, accessibility needs, or any special requests..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-amber-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-send-plane-line mr-2"></i>
                        Request Free Consultation
                      </span>
                    )}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start">
                      <i className="ri-checkbox-circle-line text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="font-semibold">Thank you for your enquiry!</p>
                        <p className="text-sm mt-1">Our travel advisors will contact you within 24 hours to discuss your personalized travel plan.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start">
                      <i className="ri-error-warning-line text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="font-semibold">Submission failed</p>
                        <p className="text-sm mt-1">Please try again or contact us directly via phone or email.</p>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Trust Indicators */}
              <div className="mt-8 grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-shield-check-line text-3xl text-amber-600 mb-2"></i>
                  <p className="text-xs font-medium text-gray-700">ATOL Protected</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-customer-service-2-line text-3xl text-amber-600 mb-2"></i>
                  <p className="text-xs font-medium text-gray-700">24/7 Support</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-star-line text-3xl text-amber-600 mb-2"></i>
                  <p className="text-xs font-medium text-gray-700">15+ Years Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Book With Us Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Book Your Consultation?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our expert advisors provide personalized guidance to ensure your journey is perfectly tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Expert Guidance</h3>
              <p className="text-sm text-gray-600">Personalized advice from experienced travel professionals</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-price-tag-3-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Best Prices</h3>
              <p className="text-sm text-gray-600">Access to exclusive deals and competitive rates</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Tailored Itineraries</h3>
              <p className="text-sm text-gray-600">Custom travel plans designed around your preferences</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Peace of Mind</h3>
              <p className="text-sm text-gray-600">ATOL protected bookings with full support</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
